import React, { useState, useEffect, useRef, act } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from '@app/store';
import {
  fetchSectorById,
  createSector,
  updateSector,
  SectorActions,
} from "./SectorSlice";
import { fetchAffiliates } from "../affiliate/AffiliateSlice";
import { fetchUserProfile } from "../auth/authSlice";
import { SectorMapper } from "./SectorMapper";
import { SectorOutputDTO } from './types';
import { AffiliateOutputDTO } from "../affiliate/types";
import { DataForm, DataTable } from '@components/ui';
import { Field } from '@components/ui/DataForm';
import { FormSkeleton } from '@components/ui/FormSkeleton';
import { ApiError } from '@/types/api';
import { useNotification } from '@hooks/useNotification';
import { Box, Typography, Alert, IconButton } from "@mui/material";
import { useAuth } from '@hooks/useAuth';
import { fetchAreas } from "../area";
import { PlusIcon } from "lucide-react";
import { Button } from "@components/ui/button";
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Separator } from "@/components/ui/separator";
import AreaForm from "../area/AreaForm";

const SectorForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.sector
  );
  const { items: affiliates, loading: affiliatesLoading } = useSelector(
    (state: RootState) => state.affiliate
  );

  const { items: areas, loading: areasLoading } = useSelector(
    (state: RootState) => state.area
  );
  console.log('areas',areas)
  // Get current Sector info from auth state
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<Partial<SectorOutputDTO>>({});
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showModalCategory, setShowModalCategory] = useState(false);

  const [showModalAddDocument,setShowModalAddDocument] = useState(false)
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);

  // Make sure we have the current Sector data
  useEffect(() => {
    if (isAuthenticated && !user) {
      console.log("🔄 Fetching current Sector profile");
      dispatch(fetchUserProfile());
    }
  }, [dispatch, isAuthenticated, user]);

  // Debug output for current Sector
  useEffect(() => {
    console.log("🧪 Current authenticated Sector:", user);
  }, [user]);

  // Sector Sector-based access control
  
  // Redirect lawyer Sectors away from this form
  useEffect(() => {
  }, [navigate, notification]);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 Iniciando carregamento do Usuário:", id);
        isLoadingRef.current = true;

        // Load affiliates
        console.log("📡 SectorForm - Carregando Afiliados");
        await dispatch(fetchAffiliates({}));
        await dispatch(fetchAreas({}));


        if (id) {
          console.log("📡 SectorForm - Carregando Usuário:", id);
          const resultAction = await dispatch(fetchSectorById(id));

          if (fetchSectorById.fulfilled.match(resultAction)) {
            console.log("✅ Usuário carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar Usuário:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(SectorActions.clearCurrentItem());
    };
  }, [dispatch, id, notification]);

  useEffect(() => {
    if (currentItem ) {
      console.log("📝 Sector - Atualizando dados do formulário:", currentItem);
      
      setFormData({
        name: currentItem.name,
        areas: currentItem.areas,
        enterpriseId: currentItem.enterpriseId,
      });
    }
  }, [currentItem]);

  // Filter available Sector options based on current Sector's Sector
  

  // Determine if enterprise_id field should be disabled
  const isEnterpriseIdDisabled = () => {
    // Only ADMIN can modify enterprise_id
    return false
  };

  const fields: Field[] = [
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      validationError: formError?.validationErrors?.name?.message,
    },
   
    {
      name: "enterpriseId",
      label: "Empresa",
      type: "select",
      required: true,
      disabled: isEnterpriseIdDisabled(),
      options: affiliates.map((affiliate: AffiliateOutputDTO) => ({
        value: affiliate._id || "",
        label: affiliate.name || "Sem nome",
      })),
      validationError: formError?.validationErrors?.enterpriseId?.message,
    },
  ];

  const handleSubmit = async (data: Partial<SectorOutputDTO>) => {
    if (submitAttemptRef.current || showModalAddDocument) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);
      // If password is empty in edit mode, remove it from the payload
      
      // If the Sector is a Manager trying to create an Admin, prevent it

      // If the Sector is a Manager, keep the existing enterpriseId or set the current Sector's enterpriseId
      // if (currentSectorSector === UserSector.ADMIN || currentSectorSector === UserSector.SUPER || currentSectorSector === UserSector.MANAGER) {
      //   if (id && formData.enterpriseId) {
      //     // Keep the original enterprise ID when editing
      //     data.enterpriseId = formData.enterpriseId;
      //   } else if (!id) {
      //     // Set the manager's own enterprise ID for new Sectors
      //     data.enterpriseId = Sector?.enterpriseId;
      //   }
      // }
      data.areas = formData.areas ?? []
      console.log('dados',data,formData.areas)
      const mappedData = SectorMapper.toDTO(data);
      console.log("📤 Enviando dados:", mappedData);

      const resultAction = id
        ? await dispatch(updateSector({ id, data: mappedData }))
        : await dispatch(createSector(mappedData));

      if (
        updateSector.rejected.match(resultAction) ||
        createSector.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        setFormError(error);
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      navigate("/Sectors");
    } catch (error) {
      console.error("❌ SectorForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  // Return nothing if Sector is a lawyer (will be redirected)

  if (isInitialLoad && (loading || affiliatesLoading)) {
    return <FormSkeleton fields={6} />;
  }

  let areas_parsed = []
  areas.map((item)=>{
    if(formData?.areas?.includes(item._id)){
      areas_parsed.push(item);
    }
  })

  return (
    <Box sx={{ p: 0 }}>
      {/* <Typography variant="h5" gutterBottom>
        {id ? "Editar Função" : "Nova Função"}
      </Typography> */}
      
      {/* {currentSectorSector === UserSector.MANAGER && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Como gerente, você não pode criar usuários administradores nem alterar a empresa vinculada ao usuário.
        </Alert>
      )}
       */}
      <DataForm
        data={formData}
        fields={fields}
        onSubmit={handleSubmit}
        basePath="Sectors"
        title={id ? "Editar Setor" : "Novo Setor"}
        error={formError}
        isEdit={id!=undefined}

        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
        children={()=><>
        <div className="flex flex-col items-start gap-1.5 w-full">
          <div className="flex w-full h-9 items-center justify-between mb-5">
            <h1 className="text-xl font-bold" id="atividades">
              Áreas ({formData.areas?.length ?? 0})
            </h1>

            <div className="flex items-center gap-5">
              <Button
                size="icon"
                type="button"
                className="w-[30px] h-[30px] bg-blue-500 rounded-lg"
                onClick={() => {
                  // setShowSelect(!showSelect)
                  setShowModalAddDocument(true)
                }}
              >
                <PlusIcon className="h-4 w-4 text-white" />
              </Button>
            </div>
          </div>

           <Dialog open={showModalCategory} onClose={() => {
                  setShowModalCategory(false)
                }}>
                  <DialogTitle>
                    <h1 className="text-xl font-bold mb-6">
                      Cadastrar nova área
                    </h1>
                    <IconButton
                      aria-label="close"
                      onClick={() => {
                        setShowModalCategory(false)
                      }}
                      sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent>
                    <AreaForm onClose={async () => {
                      setShowModalCategory(false);
                      // loadDocumentos();
                      await dispatch(fetchAreas({}));
                    }} />
                  </DialogContent>
                  {/* <DialogActions>
                  {actions || (
                    <Button onClick={onClose} color="primary">
                      Fechar
                    </Button>
                  )}
                </DialogActions> */}
                </Dialog>
           {showModalAddDocument==true && <Dialog fullWidth maxWidth="lg" open={showModalAddDocument} onClose={() => {
                  setShowModalAddDocument(false)
                }}>
                  <DialogTitle>
                    <h1 className="text-2xl font-bold mb-6">
                      Adicionar e vincular áreas
                    </h1>
                    <IconButton
                      aria-label="close"
                      onClick={() => {
                        setShowModalAddDocument(false)
                      }}
                      sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent >
                    {/* <DocumentForm onClose={async () => {
                      setShowModalDocument(false);
                      loadDocumentos();
                    }} /> */}
                    <div className="grid grid-cols-1" style={{ padding: 10, backgroundColor: '#f2f2f2', margin: 2, height: '50vh' }}>
                      <div className="scrollbar-hide" style={{ backgroundColor: 'white', borderRadius: 10, position: 'relative', margin: 2, height: '100%', overflow: 'scroll' }}>
                        <div style={{ padding: 5 }}>
          
                          <p style={{ display: 'flex', alignItems: 'center', marginTop: 30, zIndex: 2,justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', zIndex: 2 }}>

                            <svg id="atividades-img" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 5 }} width={18} height="18" viewBox="0 0 15 20">
                            <path d="M17.6307 0.00157104C14.117 -0.0614313 10.1098 1.77539 7.57338 4.62475C5.14943 4.6705 2.78923 5.66429 1.05152 7.4021C0.950272 7.50185 0.914273 7.65111 0.958522 7.78612C1.00352 7.92187 1.12052 8.02012 1.26152 8.04037L4.15195 8.45439L3.79496 8.85416C3.66221 9.00266 3.66896 9.22842 3.80996 9.36943L8.6301 14.1899C8.70285 14.2626 8.79885 14.2994 8.8956 14.2994C8.98485 14.2994 9.07409 14.2679 9.14534 14.2041L9.54508 13.8471L9.95907 16.7377C9.97932 16.8787 10.0903 16.9822 10.2246 17.0272C10.2591 17.0385 10.2951 17.0437 10.3318 17.0437C10.4383 17.0437 10.5471 16.9972 10.6221 16.923C12.3358 15.2091 13.3295 12.8488 13.3752 10.4247C16.2274 7.88287 18.0791 3.87697 17.9974 0.367585C17.9921 0.168077 17.8309 0.00682124 17.6307 0.00157104ZM14.1747 6.47582C13.8095 6.84108 13.3295 7.02409 12.8488 7.02409C12.368 7.02409 11.888 6.84108 11.5228 6.47582C10.7923 5.74454 10.7923 4.55499 11.5228 3.82372C12.254 3.09244 13.4435 3.09244 14.1747 3.82372C14.906 4.55499 14.906 5.74529 14.1747 6.47582Z" fill="#7D8592" />
                            <path d="M2.043 12.6785C1.24052 13.4811 0.13879 17.1067 0.0157927 17.517C-0.0239564 17.649 0.0127928 17.7922 0.109541 17.8897C0.181539 17.9618 0.276787 18 0.375035 18C0.411034 18 0.447033 17.9948 0.483032 17.9843C0.893273 17.8612 4.51869 16.7595 5.32118 15.9569C6.22491 15.0531 6.22491 13.5823 5.32118 12.6785C4.4167 11.7748 2.94673 11.7755 2.043 12.6785Z" fill="#7D8592" />
                          </svg> <b>Áreas ({areas.length})</b>
                            </div>

                          <div className="flex items-center gap-5">
                                            <Button
                                              size="icon"
                                              className="w-[20px] h-[20px] bg-blue-500 rounded-md"
                                              onClick={() => {
                                                // setShowSelect(!showSelect)
                                                setShowModalCategory(true)
                                              }}
                                            >
                                              <PlusIcon className="h-4 w-4 text-white" />
                                            </Button>
                                          </div>
                          </p>
                        </div>
          
                        {areas.map((activity, index) => (
                          <div key={index} style={{ textOverflow: 'ellipsis', padding: 5, cursor: 'pointer' }} onClick={(e) => {
                            // setActiveActivityIndex(index);
                            let formData_ :any= {...formData};
                            if(!formData_.areas){
                              formData_.areas=[]
                            }
                            else{
                              formData_.areas=[...formData_.areas]
                            }
                            if(formData_.areas.includes(activity._id)){
                              formData_.areas = formData_.areas.filter((item)=>item!=activity._id)
                            }
                            else{
                              formData_.areas.push(activity._id);
                            }
                            console.log(formData_.areas)
                            setFormData(formData_);
                          }}><p style={{ color:  formData.areas && formData.areas.includes(activity._id) ? '#1777CF' : 'rgba(0, 0, 0, 0.87)', display: 'flex', justifyContent: 'space-between' }}><span>{index + 1}. {activity.name}</span> </p>
                            <Separator style={{ borderWidth: 0.5, marginTop: 5 }} />
                          </div>
                        ))}
                      </div>
                     
                    </div>
                  </DialogContent>
                  {/* <DialogActions>
                  {actions || (
                    <Button onClick={onClose} color="primary">
                      Fechar
                    </Button>
                  )}
                </DialogActions> */}
                </Dialog>}
          </div>
          <div className="w-full">
                    <DataTable
                      hide_search
                      hide_add
                      hideEdit
                      columns={[
                        {
                          image: 'photoUrl',
                          placeholderImage: '/documents.svg',
                          style_image: { borderRadius: 10, border: '1px solid lightgrey', background: 'rgb(244, 244, 244)', width: 30, height: 30, objectFit: 'contain' },
          
                          key: 'name',
                          header: 'Nome',
                          render: (value: any) => String(value || ''),
                        },
                      ]}
                      hide_header
                      data={areas_parsed ?? []}
                      basePath="areas"
                      onDelete={(e) => {
                        // let documents_: any = { ...activeActivity.documents }
                        // delete documents_[e];
                        // const updatedActivity = { ...activeActivity, ['documents']: documents_ };
                        let formData_ = {...formData};
                            if(!formData_.areas){
                              formData_.areas=[]
                            }
                              formData_.areas = formData_.areas.filter((item)=>item!=e)
                            setFormData(formData_);
          
                        // onActivityUpdate(activePhaseIndex, activeActivityIndex, updatedActivity);
                      }}
                      // error={error}
                      newFormat
                    />
                  </div>
          

        </>}
      />
    </Box>
  );
};

export default SectorForm;
